import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  Pencil,
  ToggleLeft,
  ToggleRight,
  Puzzle,
  DollarSign,
  Users,
  HardDrive,
  Search,
  Layers,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  subscriptionsAdminService,
  Plan,
  PlanAddon,
} from '@/features/branding/services/subscriptions.service';
import {
  getFeatureModuleDetails,
  listAllFeatureModules,
  moduleMatchesSearch,
} from '@/common/utils/featureModuleCatalog';
import { FeatureModuleDetailsPanel } from '@/components/system/FeatureModuleDetailsPanel';

export const Route = createFileRoute('/system/plans')({
  component: PlansPage,
});

type Tab = 'plans' | 'addons' | 'modules';

function PlansPage() {
  const [tab, setTab] = useState<Tab>('plans');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'plans', label: 'Planos Base' },
    { id: 'addons', label: 'Add-ons' },
    { id: 'modules', label: 'Módulos' },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Package className="h-6 w-6 text-indigo-400" />
          Planos e Add-ons
        </h1>
        <p className="text-gray-400 mt-1">
          Configure planos, add-ons comerciais e consulte o que cada módulo libera no sistema.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-800 pb-0">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? 'border-indigo-500 text-white'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'plans' && <PlansTab />}
      {tab === 'addons' && <AddonsTab />}
      {tab === 'modules' && <ModulesTab />}
    </div>
  );
}

// ─── Planos ───────────────────────────────────────────────────────────────────

function PlansTab() {
  const qc = useQueryClient();
  const [editPlan, setEditPlan] = useState<Plan | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['system', 'plans'],
    queryFn: () => subscriptionsAdminService.listPlans(),
  });

  const toggleMutation = useMutation({
    mutationFn: (plan: Plan) =>
      subscriptionsAdminService.updatePlan(plan.id, { isActive: !plan.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system', 'plans'] }),
    onError: () => toast.error('Erro ao atualizar plano'),
  });

  if (isLoading) return <p className="text-gray-400">Carregando planos…</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          onClick={() => setCreating(true)}
          className="bg-indigo-600 hover:bg-indigo-700 gap-2"
        >
          <Plus className="h-4 w-4" /> Novo Plano
        </Button>
      </div>

      <div className="grid gap-4">
        {plans.map((plan) => (
          <Card key={plan.id} className="bg-gray-900 border-gray-800">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white text-lg">{plan.label}</h3>
                    <Badge
                      className={plan.isActive ? 'bg-green-700 text-white' : 'bg-gray-700 text-gray-300'}
                    >
                      {plan.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 font-mono mt-0.5">{plan.name}</p>

                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <DollarSign className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                      <div>
                        <p className="text-white font-medium">
                          R$ {Number(plan.basePriceBrl).toFixed(2).replace('.', ',')}
                        </p>
                        <p className="text-xs text-gray-500">base/mês</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <Users className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                      <div>
                        <p className="text-white font-medium">{plan.includedUsers} usuários</p>
                        <p className="text-xs text-gray-500">
                          +R$ {Number(plan.pricePerExtraUserBrl).toFixed(0)}/extra
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <HardDrive className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                      <div>
                        <p className="text-white font-medium">{plan.storageGbIncluded} GB</p>
                        <p className="text-xs text-gray-500">armazenamento</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white"
                    onClick={() => setEditPlan(plan)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white"
                    disabled={toggleMutation.isPending}
                    onClick={() => toggleMutation.mutate(plan)}
                  >
                    {plan.isActive ? (
                      <ToggleRight className="h-5 w-5 text-green-400" />
                    ) : (
                      <ToggleLeft className="h-5 w-5 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {creating && (
        <PlanFormDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['system', 'plans'] });
          }}
        />
      )}

      {editPlan && (
        <PlanFormDialog
          plan={editPlan}
          onClose={() => setEditPlan(null)}
          onSaved={() => {
            setEditPlan(null);
            qc.invalidateQueries({ queryKey: ['system', 'plans'] });
          }}
        />
      )}
    </div>
  );
}

export function PlanFormDialog({
  plan,
  onClose,
  onSaved,
}: {
  plan?: Plan;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!plan;
  const [form, setForm] = useState({
    name: plan?.name ?? '',
    label: plan?.label ?? '',
    basePriceBrl: plan?.basePriceBrl ?? '',
    includedUsers: plan?.includedUsers ?? 3,
    pricePerExtraUserBrl: plan?.pricePerExtraUserBrl ?? '35',
    storageGbIncluded: plan?.storageGbIncluded ?? 10,
    sortOrder: plan?.sortOrder ?? 0,
  });

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        basePriceBrl: Number(form.basePriceBrl),
        pricePerExtraUserBrl: Number(form.pricePerExtraUserBrl),
        includedUsers: Number(form.includedUsers),
        storageGbIncluded: Number(form.storageGbIncluded),
        sortOrder: Number(form.sortOrder),
      };
      return isEdit
        ? subscriptionsAdminService.updatePlan(plan!.id, payload)
        : subscriptionsAdminService.createPlan(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Plano atualizado' : 'Plano criado');
      onSaved();
    },
    onError: () => toast.error('Erro ao salvar plano'),
  });

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md" busy={mutation.isPending}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Plano' : 'Novo Plano'}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="space-y-4"
        >
        <div className="space-y-4 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Slug (identificador)</Label>
              <Input
                value={form.name}
                onChange={set('name')}
                placeholder="base"
                disabled={isEdit}
                className={isEdit ? 'opacity-50' : ''}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Nome exibido</Label>
              <Input value={form.label} onChange={set('label')} placeholder="Plano Base" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Preço base (R$)</Label>
              <Input
                type="number"
                value={form.basePriceBrl}
                onChange={set('basePriceBrl')}
                placeholder="200"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Usuários inclusos</Label>
              <Input
                type="number"
                value={form.includedUsers}
                onChange={set('includedUsers')}
                placeholder="3"
              />
            </div>
            <div className="space-y-1.5">
              <Label>R$/usuário extra</Label>
              <Input
                type="number"
                value={form.pricePerExtraUserBrl}
                onChange={set('pricePerExtraUserBrl')}
                placeholder="35"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Armazenamento (GB)</Label>
              <Input
                type="number"
                value={form.storageGbIncluded}
                onChange={set('storageGbIncluded')}
                placeholder="10"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ordem de exibição</Label>
              <Input
                type="number"
                value={form.sortOrder}
                onChange={set('sortOrder')}
                placeholder="1"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={!form.name || !form.label || !form.basePriceBrl}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {isEdit ? 'Salvar alterações' : 'Criar plano'}
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Módulos (catálogo técnico) ───────────────────────────────────────────────

function ModulesTab() {
  const [search, setSearch] = useState('');
  const allModules = listAllFeatureModules();

  const filtered = allModules.filter((mod) =>
    moduleMatchesSearch(mod.featureKey, mod.label, search),
  );

  return (
    <div className="space-y-4 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-400">
          {filtered.length} de {allModules.length} módulos
        </p>
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, feature key, permissão ou menu…"
            className="pl-9 bg-gray-900 border-gray-800 text-white placeholder:text-gray-500"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhum módulo encontrado para esta busca.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((mod) => {
            const { permissions, menuItems } = getFeatureModuleDetails(mod.featureKey);
            return (
              <Card key={mod.featureKey} className="bg-gray-900 border-gray-800">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-indigo-950 flex items-center justify-center flex-shrink-0">
                      <Layers className="h-5 w-5 text-indigo-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{mod.label}</p>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">{mod.featureKey}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {permissions.length} permissões · {menuItems.length} itens de menu
                      </p>
                    </div>
                  </div>
                  <FeatureModuleDetailsPanel
                    featureKey={mod.featureKey}
                    tone="system-dark"
                    listMaxHeight="max-h-64"
                  />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Add-ons ──────────────────────────────────────────────────────────────────

function AddonModuleDetails({ featureKey }: { featureKey: string }) {
  return (
    <div className="mt-4 pt-4 border-t border-gray-800">
      <FeatureModuleDetailsPanel featureKey={featureKey} tone="system-dark" />
    </div>
  );
}

function AddonsTab() {
  const qc = useQueryClient();
  const [editAddon, setEditAddon] = useState<PlanAddon | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: addons = [], isLoading } = useQuery({
    queryKey: ['system', 'addons'],
    queryFn: () => subscriptionsAdminService.listAddons(),
  });

  const toggleMutation = useMutation({
    mutationFn: (addon: PlanAddon) =>
      subscriptionsAdminService.updateAddon(addon.id, { isActive: !addon.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system', 'addons'] }),
    onError: () => toast.error('Erro ao atualizar add-on'),
  });

  if (isLoading) return <p className="text-gray-400">Carregando add-ons…</p>;

  const sortedAddons = [...addons].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="space-y-4 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-400">
          {sortedAddons.length} add-on{sortedAddons.length === 1 ? '' : 's'} no catálogo
        </p>
        <Button
          onClick={() => setCreating(true)}
          className="bg-indigo-600 hover:bg-indigo-700 gap-2"
        >
          <Plus className="h-4 w-4" /> Novo Add-on
        </Button>
      </div>

      {sortedAddons.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhum add-on cadastrado.</p>
      ) : (
      <div className="space-y-3">
        {sortedAddons.map((addon) => (
          <Card key={addon.id} className="bg-gray-900 border-gray-800">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="h-9 w-9 rounded-lg bg-indigo-950 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Puzzle className="h-5 w-5 text-indigo-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-white text-sm">{addon.name}</p>
                      <Badge
                        className={
                          addon.isActive
                            ? 'bg-green-800 text-green-200'
                            : 'bg-gray-800 text-gray-400'
                        }
                      >
                        {addon.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 font-mono mt-0.5">{addon.featureKey}</p>
                    {addon.description && (
                      <p className="text-xs text-gray-400 mt-1">{addon.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="text-right">
                    <p className="font-semibold text-white text-sm">
                      R$ {Number(addon.priceBrl).toFixed(2).replace('.', ',')}
                    </p>
                    <p className="text-xs text-gray-500">/mês</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-white h-8 w-8 p-0"
                      onClick={() => setEditAddon(addon)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-white h-8 w-8 p-0"
                      disabled={toggleMutation.isPending}
                      onClick={() => toggleMutation.mutate(addon)}
                    >
                      {addon.isActive ? (
                        <ToggleRight className="h-4 w-4 text-green-400" />
                      ) : (
                        <ToggleLeft className="h-4 w-4 text-gray-500" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <AddonModuleDetails featureKey={addon.featureKey} />
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {creating && (
        <AddonFormDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['system', 'addons'] });
          }}
        />
      )}

      {editAddon && (
        <AddonFormDialog
          addon={editAddon}
          onClose={() => setEditAddon(null)}
          onSaved={() => {
            setEditAddon(null);
            qc.invalidateQueries({ queryKey: ['system', 'addons'] });
          }}
        />
      )}
    </div>
  );
}

function AddonFormDialog({
  addon,
  onClose,
  onSaved,
}: {
  addon?: PlanAddon;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!addon;
  const [form, setForm] = useState({
    featureKey: addon?.featureKey ?? '',
    name: addon?.name ?? '',
    description: addon?.description ?? '',
    priceBrl: addon?.priceBrl ?? '',
    sortOrder: addon?.sortOrder ?? 0,
  });

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { ...form, priceBrl: Number(form.priceBrl) };
      return isEdit
        ? subscriptionsAdminService.updateAddon(addon!.id, payload)
        : subscriptionsAdminService.createAddon(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Add-on atualizado' : 'Add-on criado');
      onSaved();
    },
    onError: () => toast.error('Erro ao salvar add-on'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md" busy={mutation.isPending}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Add-on' : 'Novo Add-on'}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="space-y-4"
        >
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Feature Key</Label>
            <Input
              value={form.featureKey}
              onChange={(e) => setForm((f) => ({ ...f, featureKey: e.target.value }))}
              placeholder="WHATSAPP_INTEGRATION"
              disabled={isEdit}
              className={`font-mono ${isEdit ? 'opacity-50' : ''}`}
            />
            <p className="text-xs text-muted-foreground">
              Deve coincidir exatamente com o enum FeatureKey no backend.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Nome exibido</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Integração WhatsApp"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Descrição</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Descreva o que este módulo oferece…"
              rows={2}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Preço mensal (R$)</Label>
              <Input
                type="number"
                value={form.priceBrl}
                onChange={(e) => setForm((f) => ({ ...f, priceBrl: e.target.value }))}
                placeholder="89"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ordem de exibição</Label>
              <Input
                type="number"
                value={form.sortOrder}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))
                }
                placeholder="1"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={!form.featureKey || !form.name || !form.priceBrl}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {isEdit ? 'Salvar alterações' : 'Criar add-on'}
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
